<script setup lang="ts">
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ORDERING, ORDERING_CUSTOM, ORDERING_CUSTOM_LABEL, orderingLabel, type OrderingChoice } from '@/utils/customPlaylistSchedule';
import OrderingIcon from './OrderingIcon.vue';
import { SHOW_ORDERING_ICONS } from './orderingIcons';

/**
 * How a schedule plays its list: the four orderings the players know, each setting
 * should_shuffle, resume_from_file_offset and loop together. "Custom (keep current)" is only
 * offered while it is the current value, for a saved row whose three columns match none of
 * the four. The schedule editor and the bulk edit dialog both use it.
 */
withDefaults(
    defineProps<{
        id?: string;
        disabled?: boolean;
        placeholder?: string;
        testId?: string;
    }>(),
    { id: undefined, disabled: false, placeholder: 'Ordering…', testId: 'schedule-ordering' },
);

const ordering = defineModel<OrderingChoice | null>({ default: null });
</script>

<template>
    <Select :model-value="ordering ?? undefined" :disabled="disabled" @update:model-value="(value) => (ordering = value as OrderingChoice)">
        <SelectTrigger :id="id" class="w-full" :data-testid="testId">
            <!-- The chosen option reads the same closed as it does in the open menu. -->
            <SelectValue :placeholder="placeholder">
                <span v-if="ordering" class="flex items-center gap-2">
                    <OrderingIcon v-if="SHOW_ORDERING_ICONS" :choice="ordering" class="size-4 shrink-0" />
                    {{ orderingLabel(ordering) }}
                </span>
            </SelectValue>
        </SelectTrigger>
        <SelectContent>
            <SelectItem v-if="ordering === ORDERING_CUSTOM" :value="ORDERING_CUSTOM">
                <span class="flex items-center gap-2">
                    <OrderingIcon v-if="SHOW_ORDERING_ICONS" :choice="ORDERING_CUSTOM" class="size-4 shrink-0" />
                    {{ ORDERING_CUSTOM_LABEL }}
                </span>
            </SelectItem>
            <SelectItem v-for="option in ORDERING" :key="option.value" :value="option.value" :data-testid="`${testId}-option-${option.value}`">
                <span class="flex items-center gap-2">
                    <OrderingIcon v-if="SHOW_ORDERING_ICONS" :choice="option.value" class="size-4 shrink-0" />
                    {{ option.label }}
                </span>
            </SelectItem>
        </SelectContent>
    </Select>
</template>
